/**
 * SecurityIntegration.jsx
 * =============================================================
 * MSN-CLIQ vEritEqcLiQ RETAIL ERP — Single Drop-in Security File
 * =============================================================
 *
 * HOW TO INTEGRATE INTO ANY REACT (Vite or CRA) PROJECT:
 * -------------------------------------------------------
 * 1. Copy this file into your project:  src/security/SecurityIntegration.jsx
 * 2. Set environment variable in your .env:
 *      VITE_SECURITY_API=https://your-security-module-url/api   (Vite)
 *      REACT_APP_SECURITY_API=https://your-security-module-url/api (CRA)
 * 3. In your root layout / App.jsx, wrap your app:
 *      import { SecurityGate } from './security/SecurityIntegration';
 *      <SecurityGate>  <YourApp />  </SecurityGate>
 * 4. In any form / page header, add the date-time badge:
 *      import { SecurityStatusBar } from './security/SecurityIntegration';
 *      <SecurityStatusBar />        ← place in top-right of any toolbar
 * 5. Optionally protect individual forms with:
 *      import { withSecurityGuard } from './security/SecurityIntegration';
 *      const ProtectedForm = withSecurityGuard(YourFormComponent);
 *
 * That's it — one file, zero other dependencies beyond React.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE as API } from '../lib/api';
import './SecurityStyles.css';

// ─── config ──────────────────────────────────────────────────────────────────
const SECURITY_API =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SECURITY_API) ||
  process.env.REACT_APP_SECURITY_API ||
  '';

// ─── context ─────────────────────────────────────────────────────────────────
const SecurityCtx = createContext({
  verified:    false,
  fingerprint: '',
  guardStatus: null,
  mssql:       false,
  loading:     true,
  refresh:     () => {}
});

export function useSecurityGuard() { return useContext(SecurityCtx); }

// ─── provider ─────────────────────────────────────────────────────────────────
export function SecurityGate({ children, loadingNode, blockedNode }) {
  const [state, setState] = useState({ verified: false, fingerprint: '', guardStatus: null, mssql: false, loading: true });

  const check = useCallback(async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      const endpoint = SECURITY_API
        ? `${SECURITY_API}/health`
        : `${API}/api/security/health`;
      const r = await fetch(endpoint);
      const d = await r.json();
      setState({
        verified:    d.status === 'OK',
        fingerprint: d.fingerprint || '',
        guardStatus: d.guardStatus || null,
        mssql:       d.mssqlConnected || false,
        loading:     false
      });
    } catch {
      // Fail-open: let app start when security endpoint is unreachable
      setState({ verified: true, fingerprint: 'OPEN_MODE', guardStatus: null, mssql: false, loading: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // SECURITY_API is a module-level constant — stable, intentionally omitted

  useEffect(() => { check(); }, [check]);

  // useMemo MUST be called unconditionally before any early returns (Rules of Hooks)
  const ctxValue = useMemo(
    () => ({ ...state, refresh: check }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.verified, state.fingerprint, state.guardStatus, state.mssql, state.loading, check]
  );

  if (state.loading) {
    return loadingNode || <SecurityLoadingScreen />;
  }
  if (!state.verified) {
    return blockedNode || <SecurityBlockedScreen reason={state.guardStatus?.reason} onRetry={check} />;
  }

  return (
    <SecurityCtx.Provider value={ctxValue}>
      {children}
    </SecurityCtx.Provider>
  );
}

// ─── SecurityDateTime ─────────────────────────────────────────────────────────
/**
 * Drop into any top-bar / header to show live date & time.
 * The | separator has a vertical gradient (blue → violet).
 *
 * Usage:
 *   <SecurityDateTime />
 *   <SecurityDateTime format24h={false} showSeconds={false} />
 */
export function SecurityDateTime({ format24h = true, showSeconds = true, style = {} }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const weekday = now.toLocaleDateString('en-IN', { weekday: 'short' });
  const date    = now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  const time    = now.toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit',
    ...(showSeconds ? { second: '2-digit' } : {}),
    hour12: !format24h
  });

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0,
      fontFamily: '"IBM Plex Mono", "Fira Code", monospace',
      fontSize: 12,
      fontWeight: 600,
      color: '#334155',
      letterSpacing: '0.03em',
      userSelect: 'none',
      ...style
    }}>
      {/* Date part */}
      <span style={{ color: '#475569', whiteSpace: 'nowrap' }}>
        {weekday}, {date}
      </span>

      {/* Gradient | separator */}
      <GradientSeparator />

      {/* Time part */}
      <span style={{ color: '#0f172a', fontWeight: 700, whiteSpace: 'nowrap' }}>
        {time}
      </span>

      {/* Live pulse dot */}
      <span style={{
        marginLeft: 6,
        width: 5,
        height: 5,
        borderRadius: '50%',
        background: '#10b981',
        boxShadow: '0 0 6px rgba(16,185,129,0.7)',
        animation: 'si_pulse 2s ease-in-out infinite',
        flexShrink: 0
      }} />

      <style>{`
        @keyframes si_pulse {
          0%,100% { opacity:1; transform:scale(1);   }
          50%      { opacity:.4; transform:scale(.8); }
        }
      `}</style>
    </div>
  );
}

// gradient | pipe
function GradientSeparator() {
  return (
    <span
      aria-hidden="true"
      style={{
        display:        'inline-block',
        width:          2,
        height:         18,
        margin:         '0 10px',
        borderRadius:   2,
        flexShrink:     0,
        background:     'linear-gradient(180deg, #3b82f6 0%, #8b5cf6 60%, #ec4899 100%)',
        boxShadow:      '0 0 6px rgba(139,92,246,0.45)',
        verticalAlign:  'middle',
        position:       'relative',
        top:            '-1px'
      }}
    />
  );
}

// ─── SecurityStatusBar ────────────────────────────────────────────────────────
/**
 * Full right-side topbar segment: DNA badge + date/time.
 * Replace or extend your existing topbar's right section with this.
 *
 * Usage:
 *   <div style={{ display:'flex', alignItems:'center', gap:12 }}>
 *     ... your other topbar items ...
 *     <SecurityStatusBar />
 *   </div>
 */
export function SecurityStatusBar({ showFingerprint = false, style = {} }) {
  const { verified, fingerprint, guardStatus, mssql } = useSecurityGuard();
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, ...style }}>
      {/* Guard chip */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        borderRadius: 100,
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        border: '1px solid',
        background: verified ? '#f0fdf4' : '#fef2f2',
        color:      verified ? '#16a34a' : '#dc2626',
        borderColor:verified ? '#bbf7d0' : '#fecaca'
      }}>
        <span style={{ width:5, height:5, borderRadius:'50%', background: verified ? '#10b981' : '#ef4444', display:'inline-block' }} />
        {verified ? 'DNA Verified' : (guardStatus?.reason || 'LOCKED')}
      </div>

      {/* MSSQL chip */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 9px',
        borderRadius: 100,
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        border: '1px solid',
        background:  mssql ? '#eff6ff' : '#fffbeb',
        color:       mssql ? '#2563eb' : '#d97706',
        borderColor: mssql ? '#bfdbfe' : '#fde68a'
      }}>
        {mssql ? 'MSSQL ✓' : 'File Mode'}
      </div>

      {/* Gradient separator */}
      <GradientSeparator />

      {/* Live date + time */}
      <SecurityDateTime />
    </div>
  );
}

// ─── withSecurityGuard HOC ────────────────────────────────────────────────────
/**
 * Wrap any form/component to block rendering when security fails.
 *
 * Usage:
 *   const SafeInvoiceForm = withSecurityGuard(InvoiceForm);
 *   // Use SafeInvoiceForm exactly like InvoiceForm
 */
export function withSecurityGuard(WrappedComponent, options = {}) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  function Guarded(props) {
    const { verified, loading, guardStatus } = useSecurityGuard();
    if (loading)   return <MiniLoadingSpinner />;
    if (!verified) return <FormBlockOverlay reason={guardStatus?.reason} label={options.label || displayName} />;
    return <WrappedComponent {...props} />;
  }
  Guarded.displayName = `withSecurityGuard(${displayName})`;
  return Guarded;
}

// ─── Internal screens / helpers ───────────────────────────────────────────────
function SecurityLoadingScreen() {
  return (
    <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', color:'#94a3b8' }}>
        <div style={{ width:36,height:36,border:'3px solid #3b82f6',borderTopColor:'transparent',borderRadius:'50%',animation:'si_spin 0.8s linear infinite',margin:'0 auto 14px' }} />
        <p style={{ fontFamily:'monospace', fontSize:11, letterSpacing:2, textTransform:'uppercase' }}>Validating DNA Signature...</p>
        <style>{`@keyframes si_spin{ to{ transform:rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

function SecurityBlockedScreen({ reason, onRetry }) {
  return (
    <div style={{ minHeight:'100vh', background:'#0f172a', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ maxWidth:440, background:'#1e293b', border:'2px solid #ef4444', borderRadius:16, padding:36, textAlign:'center' }}>
        <div style={{ fontSize:44, marginBottom:14 }}>⚠️</div>
        <h1 style={{ color:'#f8fafc', fontWeight:900, fontSize:22, marginBottom:10 }}>SECURITY ALERT</h1>
        <p style={{ color:'#94a3b8', marginBottom:20, lineHeight:1.6, fontSize:13 }}>Identity verification failed. Unauthorized session.</p>
        <div style={{ background:'#0f172a', padding:14, borderRadius:8, textAlign:'left', fontFamily:'monospace', fontSize:10, color:'#ef4444', marginBottom:22 }}>
          ERROR: IDENTITY_CHAIN_BROKEN<br/>OWNER: MSN-CLIQ<br/>CODE: {reason || 'IP_VIOLATION_911'}
        </div>
        <button onClick={onRetry} style={{ background:'#4f46e5', color:'#fff', fontWeight:700, padding:'11px 28px', borderRadius:8, border:'none', cursor:'pointer', fontSize:12 }}>RETRY CONNECTION</button>
      </div>
    </div>
  );
}

function FormBlockOverlay({ reason, label }) {
  return (
    <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'28px 24px', textAlign:'center' }}>
      <div style={{ fontSize:24, marginBottom:8 }}>🔒</div>
      <div style={{ fontWeight:700, color:'#dc2626', fontSize:13, marginBottom:6 }}>Form Access Denied</div>
      <div style={{ fontSize:11, color:'#64748b' }}>{label} is protected by MSN-CLIQ DNA Security.</div>
      <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'monospace', marginTop:8 }}>{reason || 'SIGNATURE_NOT_VERIFIED'}</div>
    </div>
  );
}

function MiniLoadingSpinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:40 }}>
      <div style={{ width:22,height:22,border:'2px solid #3b82f6',borderTopColor:'transparent',borderRadius:'50%',animation:'si_spin 0.8s linear infinite' }} />
      <style>{`@keyframes si_spin{ to{ transform:rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── re-export for convenience ────────────────────────────────────────────────
export { GradientSeparator };
export default SecurityGate;
